import React, { useMemo } from 'react';
import StatusPill from './StatusPill';
import { getField, getGrade, fmtM, receivedStr, CLOSED_STATUSES } from '../lib/utils';
import { FILTERS } from './Sidebar';

const TABS = [
  { id:'all',             label:'All Open'       },
  { id:'intake',          label:'Intake'         },
  { id:'nbi_ready',       label:'NBI Ready'      },
  { id:'data_incomplete', label:'Data Incomplete'},
  { id:'nbi_sent',        label:'NBI Sent'       },
  { id:'assessment',      label:'Assessment'     },
  { id:'ready_to_quote',  label:'Ready to Quote' },
  { id:'review_incomplete', label:'Review Incomplete' },
  { id:'quoted',          label:'Quoted'         },
  { id:'referred',        label:'Referred'       },
  { id:'bound',           label:'Bound'          },
  { id:'declined',        label:'Declined / Lost'},
];

function tabCount(submissions, id) {
  const fn = FILTERS[id];
  return fn ? submissions.filter(fn).length : 0;
}

function tabColor(id) {
  if (['bound'].includes(id))
    return { bg:'var(--green-bg)',  color:'var(--green)',  border:'var(--green-b)'  };
  if (['nbi_ready','ready_to_quote'].includes(id))
    return { bg:'var(--blue-bg)',   color:'var(--accent)', border:'var(--blue-b)'   };
  if (['data_incomplete','review_incomplete','nbi_sent','assessment','quoted','referred'].includes(id))
    return { bg:'var(--amber-bg)',  color:'var(--amber)',  border:'var(--amber-b)'  };
  if (['declined'].includes(id))
    return { bg:'var(--red-bg)',    color:'var(--red)',    border:'var(--red-b)'    };
  return   { bg:'var(--surface3)', color:'var(--muted2)', border:'var(--border)'   };
}

export default function SubmissionQueue({ submissions, loading, filter, setFilter, search, setSearch, selected, onSelect, onRefresh }) {
  const filtered = useMemo(() => {
    let subs = [...submissions];
    if (filter !== 'all') {
      subs = subs.filter(s => s.status === filter);
    }
    if (search) {
      const q = search.toLowerCase();
      subs = subs.filter(s =>
        s.reference_number?.toLowerCase().includes(q) ||
        s.broker_name?.toLowerCase().includes(q) ||
        s.broker_firm?.toLowerCase().includes(q) ||
        getField(s, 'insured_name')?.toLowerCase().includes(q) ||
        getField(s, 'state')?.toLowerCase().includes(q)
      );
    }
    return subs.sort((a, b) => new Date(b.received_at || 0) - new Date(a.received_at || 0));
  }, [submissions, filter, search]);

  return (
    <>
      {/* Page header */}
      <div style={{
        padding:'28px 32px 20px', display:'flex',
        alignItems:'flex-end', borderBottom:'1px solid var(--border)',
      }}>
        <div>
          <div style={{
            fontFamily:'var(--serif)', fontSize:22,
            fontWeight:400, letterSpacing:'-0.02em',
          }}>
            Orders
          </div>
          <div style={{ fontSize:11, color:'var(--muted)', marginTop:3 }}>
            All active orders — sorted by received date
          </div>
        </div>
        <div style={{ marginLeft:'auto' }}>
          <button
            onClick={onRefresh}
            onMouseEnter={e => {
              e.currentTarget.style.background  = 'var(--surface3)';
              e.currentTarget.style.color       = 'var(--text)';
              e.currentTarget.style.borderColor = 'var(--accent)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.background  = 'var(--surface2)';
              e.currentTarget.style.color       = 'var(--muted2)';
              e.currentTarget.style.borderColor = 'var(--border2)';
            }}
            onMouseDown={e => { e.currentTarget.style.transform = 'scale(0.97)'; }}
            onMouseUp={e => { e.currentTarget.style.transform = 'scale(1)'; }}
            style={{
              height:32, padding:'0 14px', borderRadius:4,
              border:'1px solid var(--border2)', background:'var(--surface2)',
              color:'var(--muted2)', fontSize:11,
              display:'flex', alignItems:'center', gap:6, cursor:'pointer',
              transition:'background 0.15s, color 0.15s, border-color 0.15s, transform 0.08s',
            }}
          >
            ↻ Refresh
          </button>
        </div>
      </div>

      {/* Status tabs */}
      <div style={{
        display:'flex', padding:'0 32px',
        borderBottom:'1px solid var(--border)',
        background:'var(--surface)', overflowX:'auto', scrollbarWidth:'none',
      }}>
        {TABS.map(tab => {
          const cnt    = tabCount(submissions, tab.id);
          const tc     = tabColor(tab.id);
          const active = filter === tab.id;
          return (
            <div
              key={tab.id}
              onClick={() => setFilter(tab.id)}
              style={{
                padding:'13px 14px', fontSize:11,
                color: active ? 'var(--text)' : 'var(--muted)',
                cursor:'pointer',
                borderBottom: active ? '2px solid var(--accent)' : '2px solid transparent',
                marginBottom:-1, whiteSpace:'nowrap',
                display:'flex', alignItems:'center', gap:6,
                transition:'all 0.15s',
              }}
            >
              {tab.label}
              <span style={{
                fontSize:10, padding:'1px 6px', borderRadius:10,
                background:tc.bg, color:tc.color, border:`1px solid ${tc.border}`,
              }}>
                {cnt}
              </span>
            </div>
          );
        })}
      </div>

      {/* Search + filter bar */}
      <div style={{ padding:'20px 32px', flex:1 }}>
        <div style={{ display:'flex', gap:10, marginBottom:16, alignItems:'center' }}>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search property, reference, broker..."
            style={{
              flex:1, maxWidth:320, height:32, padding:'0 12px',
              background:'var(--surface2)', border:'1px solid var(--border)',
              borderRadius:4, color:'var(--text)', fontSize:12,
            }}
          />
          <select
            onChange={e => setSearch(e.target.value)}
            style={{
              height:32, padding:'0 10px', background:'var(--surface2)',
              border:'1px solid var(--border)', borderRadius:4,
              color:'var(--muted2)', fontSize:11,
            }}
          >
            <option value="">All states</option>
            {['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA',
              'KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
              'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT',
              'VA','WA','WV','WI','WY'].map(s => <option key={s}>{s}</option>)}
          </select>
          <div style={{ marginLeft:'auto', fontSize:11, color:'var(--muted)' }}>
            {filtered.length} order{filtered.length !== 1 ? 's' : ''}
          </div>
        </div>

        <div style={{ border:'1px solid var(--border)', borderRadius:6, overflow:'hidden' }}>
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead>
              <tr>
                {['Reference','Property','State','TIV','Risk Score','NLE / PML / MFL','Status','Received','Broker'].map(h => (
                  <th key={h} style={{
                    padding:'10px 14px', textAlign:'left', fontSize:10,
                    letterSpacing:'0.08em', textTransform:'uppercase',
                    color:'var(--muted)', background:'var(--surface2)',
                    borderBottom:'1px solid var(--border)', whiteSpace:'nowrap',
                  }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={9} style={{ textAlign:'center', padding:48, color:'var(--muted)' }}>
                    Loading orders...
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={9} style={{ textAlign:'center', padding:48, color:'var(--muted)' }}>
                    No orders found
                  </td>
                </tr>
              ) : filtered.map(sub => (
                <OrderRow
                  key={sub.id}
                  sub={sub}
                  selected={selected?.id === sub.id}
                  onSelect={onSelect}
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

function OrderRow({ sub, selected, onSelect }) {
  const isNew = sub.received_at &&
    (Date.now() - new Date(sub.received_at)) < 7200000 &&
    !CLOSED_STATUSES.includes(sub.status);

  const name  = getField(sub, 'insured_name') || '—';
  const state = getField(sub, 'state') || '—';
  const occ   = getField(sub, 'occupancy_class') || '—';
  const tiv   = getField(sub, 'tiv');
  const ev    = sub.evaluation;
  const pb    = ev?.pricing_breakdown || {};
  const grade = getGrade(ev?.score_total);
  const nle   = ev?.nle_value;
  const pml   = pb.pml_value;
  const mfl   = pb.mfl_value;
  const leStr = nle ? `${fmtM(nle)} / ${fmtM(pml)} / ${fmtM(mfl)}` : '—';

  return (
    <tr
      onClick={() => onSelect(sub)}
      style={{
        borderBottom:'1px solid var(--border)', cursor:'pointer',
        background: selected ? 'var(--surface3)' : 'transparent',
        transition:'background 0.1s',
      }}
      onMouseEnter={e => !selected && (e.currentTarget.style.background = 'var(--surface2)')}
      onMouseLeave={e => !selected && (e.currentTarget.style.background = 'transparent')}
    >
      <td style={{ padding:'12px 14px' }}>
        <div style={{ display:'flex', alignItems:'center', gap:6 }}>
          <div style={{ fontSize:11, color:'var(--accent)', fontWeight:500 }}>
            {sub.reference_number}
          </div>
          {isNew && (
            <span style={{
              fontSize:9, padding:'1px 5px', borderRadius:3,
              background:'var(--amber-bg)', color:'var(--amber)',
              border:'1px solid var(--amber-b)', fontWeight:600, letterSpacing:'0.06em',
            }}>
              NEW
            </span>
          )}
        </div>
      </td>
      <td style={{ padding:'12px 14px' }}>
        <div style={{ fontSize:12, fontWeight:500 }}>{name}</div>
        <div style={{ fontSize:10, color:'var(--muted)', marginTop:2 }}>{occ}</div>
      </td>
      <td style={{ padding:'12px 14px', fontSize:12 }}>{state}</td>
      <td style={{ padding:'12px 14px', fontSize:12 }}>
        {tiv ? fmtM(parseFloat(tiv)) : '—'}
      </td>
      <td style={{ padding:'12px 14px' }}>
        <span style={{ fontSize:12, fontWeight:500, color:grade.color }}>
          {grade.label}
        </span>
      </td>
      <td style={{ padding:'12px 14px' }}>
        <span style={{ fontSize:11, color:'var(--muted2)' }}>{leStr}</span>
      </td>
      <td style={{ padding:'12px 14px' }}>
        <StatusPill submission={sub} />
      </td>
      <td style={{ padding:'12px 14px' }}>
        <span style={{ fontSize:11, color:'var(--muted2)' }}>
          {receivedStr(sub.received_at)}
        </span>
      </td>
      <td style={{ padding:'12px 14px' }}>
        <div style={{ fontSize:11, color:'var(--muted2)' }}>
          {sub.broker_name || sub.broker_firm || '—'}
        </div>
        {sub.broker_name && sub.broker_firm && sub.broker_name !== sub.broker_firm && (
          <div style={{ fontSize:10, color:'var(--muted)' }}>
            {sub.broker_firm.charAt(0).toUpperCase() + sub.broker_firm.slice(1)}
          </div>
        )}
      </td>
    </tr>
  );
}